import axios from "axios";
import { useCallback, useRef, useState } from "react";
import { normalizeCEPData } from "./Normalizers";

const cepCache = new Map();

const mapBrasilApiCep = (data = {}) =>
  normalizeCEPData({
    logradouro: data.street || "",
    bairro: data.neighborhood || "",
    localidade: data.city || "",
    uf: data.state || "",
    cep: data.cep || "",
  });

const fetchCepFromNetwork = async (digits, cancelToken) => {
  try {
    const { data } = await axios.get(`https://viacep.com.br/ws/${digits}/json/`, {
      cancelToken,
      timeout: 5000,
    });

    if (data?.erro) {
      throw new Error("CEP não encontrado");
    }

    return normalizeCEPData(data);
  } catch (viaCepError) {
    if (axios.isCancel(viaCepError)) {
      throw viaCepError;
    }

    const { data } = await axios.get(`https://brasilapi.com.br/api/cep/v1/${digits}`, {
      cancelToken,
      timeout: 5000,
    });

    return mapBrasilApiCep(data);
  }
};

export const useCEPData = () => {
  const [requestLoading, setRequestLoading] = useState(false);
  const [requestError, setRequestError] = useState(false);
  const [requestData, setRequestData] = useState({});
  const cancelRef = useRef(null);

  const resetCEP = useCallback(() => {
    setRequestData({});
    setRequestError(false);
  }, []);

  const fetchCEPData = useCallback(({ cep }) => {
    const digits = (cep || "").replace(/\D/g, "");
    if (digits.length !== 8) {
      return Promise.resolve(null);
    }

    if (cepCache.has(digits)) {
      return Promise.resolve(cepCache.get(digits));
    }

    if (cancelRef.current) {
      cancelRef.current.cancel("nova busca de CEP");
    }

    const source = axios.CancelToken.source();
    cancelRef.current = source;

    setRequestError(false);
    setRequestLoading(true);

    return fetchCepFromNetwork(digits, source.token)
      .then((values) => {
        cepCache.set(digits, values);
        setRequestData(values);
        return values;
      })
      .catch((err) => {
        if (!axios.isCancel(err)) {
          setRequestError(true);
        }
        return null;
      })
      .finally(() => {
        setRequestLoading(false);
        if (cancelRef.current === source) {
          cancelRef.current = null;
        }
      });
  }, []);

  return { requestData, requestError, requestLoading, fetchCEPData, resetCEP };
};
